import { Routes } from '@angular/router';

import { UserRoles } from '@troyai/auth/data-access';
import {
  AuthCallbackViewComponent,
  AuthenticatedGuard,
  EmailVerificationComponent,
  FeatureFlagsGuard,
  FeatureFlagsRedirectGuard,
  PublicGuard,
  RoleGuard,
  TenantGuard,
} from '@troyai/auth/feature';
import {
  AccessDeniedShellComponent,
  LoginShellComponent,
  LogoutShellComponent,
  UnitedAdherenceProgramMessage,
} from '@troyai/auth/shell';
import { EPISODE_FEATURE_FLAGS } from '@troyai/envs';
import { Layouts, setLayout } from '@troyai/layout';
import { OnboardingGuard } from '@troyai/onboarding/feature';
import { demoHomeGuard, demoModeGuard } from '@troyai/portal/investor-demo/core';

export const APP_ROUTES: Routes = [
  {
    path: 'patients',
    loadChildren: () => import('@troyai/patients/feature').then((m) => m.PATIENTS_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, RoleGuard, OnboardingGuard()],
    data: {
      requiredRoles: [
        UserRoles.PharmacyUser,
        UserRoles.CMExternal,
        UserRoles.CMInternal,
        UserRoles.CareTeamHRA,
      ],
    },
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Patients',
  },
  {
    path: 'actions',
    loadChildren: () => import('@troyai/actions/feature').then((m) => m.ACTIONS_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, RoleGuard, OnboardingGuard()],
    data: {
      requiredRoles: [UserRoles.PharmacyUser],
    },
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Actions',
  },

  {
    path: 'adherence',
    loadChildren: () => import('@troyai/adherence/feature').then((m) => m.ADHERENCE_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, OnboardingGuard()],
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Adherence',
  },
  {
    path: 'prior-authorization',
    loadChildren: () =>
      import('@troyai/prior-authorization/feature').then((m) => m.PRIOR_AUTHORIZATION_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, RoleGuard, OnboardingGuard()],
    data: {
      requiredRoles: [UserRoles.PharmacyUser],
    },
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Prior Authorizations',
  },
  {
    path: 'leaderboard',
    loadChildren: () => import('@troyai/leaderboard/feature').then((m) => m.LEADERBOARD_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, RoleGuard, OnboardingGuard()],
    data: {
      requiredRoles: [UserRoles.PharmacyUser],
    },
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Leaderboard',
  },
  {
    path: 'resources',
    loadChildren: () => import('@troyai/resources/feature').then((m) => m.RESOURCES_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, OnboardingGuard()],
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Resources',
  },
  {
    path: 'care',
    loadChildren: () => import('@troyai/portal/hra/hra-shell').then((m) => m.HRA_CARE_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, RoleGuard, OnboardingGuard()],
    data: {
      requiredRoles: [UserRoles.CareTeamHRA, UserRoles.PharmacyUser, UserRoles.CMExternal],
    },
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Care',
  },
  {
    path: 'episodes',
    loadChildren: () =>
      import('@troyai/portal/intervention-episodes/shell').then((m) => m.EPISODE_WORKLIST_ROUTES),
    canActivate: [
      TenantGuard(),
      AuthenticatedGuard,
      RoleGuard,
      OnboardingGuard(),
      FeatureFlagsGuard(),
    ],
    data: {
      requiredRoles: [UserRoles.CMInternal, UserRoles.CMExternal, UserRoles.PharmacyUser],
      requiredFeatureFlags: EPISODE_FEATURE_FLAGS,
    },
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Episodes',
  },
  {
    path: 'activity',
    loadChildren: () => import('@troyai/activity/shell').then((m) => m.ACTIVITY_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard],
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Activity',
  },
  {
    path: 'settings',
    loadChildren: () => import('@troyai/user-profile/feature').then((m) => m.USER_PROFILES_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard],
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Settings',
  },
  {
    path: 'notifications',
    loadChildren: () => import('@troyai/notifications/feature').then((m) => m.NOTIFICATIONS_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, OnboardingGuard()],
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Notifications',
  },
  {
    path: 'login',
    component: LoginShellComponent,
    canActivate: [TenantGuard(), PublicGuard],
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    title: 'Login',
  },
  {
    path: 'logout',
    component: LogoutShellComponent,
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    canActivate: [PublicGuard],
  },
  {
    path: 'forbidden',
    component: AccessDeniedShellComponent,
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    title: 'Forbidden',
  },
  {
    path: 'onboarding',
    loadChildren: () => import('@troyai/onboarding/shell').then((m) => m.ONBOARDING_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard],
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    title: 'Onboarding',
  },
  {
    path: 'auth-callback',
    component: AuthCallbackViewComponent,
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    title: 'Authenticating...',
  },
  {
    path: 'check-email',
    component: EmailVerificationComponent,
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    title: 'Check email',
  },
  {
    path: 'welcome/:base64InviteData',
    loadComponent: () =>
      import('@troyai/onboarding/shell').then((x) => x.WelcomeViewShellComponent),
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    title: 'Welcome to Troy.AI',
  },
  {
    path: 'care-management',
    loadChildren: () =>
      import('@troyai/care-management/shell').then((m) => m.CARE_MANAGEMENT_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, RoleGuard],
    data: {
      requiredRoles: [UserRoles.CMInternal, UserRoles.CMExternal],
    },
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Care Management',
  },
  {
    path: 'download/:memberId/:fileId',
    loadComponent: () =>
      import('@troyai/patients/feature').then((m) => m.PatientFileDownloaderComponent),
    canActivate: [TenantGuard(), AuthenticatedGuard],
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    title: 'Download Patient File',
  },
  {
    path: 'external',
    loadChildren: () =>
      import('@troyai/portal/assessments-viewer/shell').then((m) => m.ASSESSMENTS_VIEWER_ROUTES),
    canActivate: [TenantGuard(), AuthenticatedGuard, RoleGuard],
    data: {
      requiredRoles: [UserRoles.HealthAssessmentViewer],
    },
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Members with assessments',
  },
  {
    path: 'united-adherence-program',
    component: UnitedAdherenceProgramMessage,
    canActivate: [],
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    title: 'Thank you - Adherence Program',
  },
  // Investor demo (TRO-2301): both routes send visitors who are not in demo mode to the home page.
  {
    path: 'care-console',
    loadChildren: () =>
      import('@troyai/portal/investor-demo/pages').then((m) => m.CARE_CONSOLE_DEMO_ROUTES),
    canActivate: [demoModeGuard, TenantGuard(), AuthenticatedGuard, OnboardingGuard()],
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Care Console',
  },
  {
    path: 'ledger',
    loadChildren: () =>
      import('@troyai/portal/investor-demo/pages').then((m) => m.LEDGER_DEMO_ROUTES),
    canActivate: [demoModeGuard, TenantGuard(), AuthenticatedGuard, OnboardingGuard()],
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    title: 'Ledger',
  },
  {
    path: '',
    loadChildren: () => import('@troyai/dashboard/feature').then((m) => m.DASHBOARD_ROUTES),
    resolve: {
      layout: setLayout(Layouts.Sidebar),
    },
    pathMatch: 'full',
    canActivate: [
      demoHomeGuard,
      TenantGuard(),
      AuthenticatedGuard,
      RoleGuard,
      OnboardingGuard(),
      FeatureFlagsRedirectGuard(),
    ],
    data: {
      requiredRoles: [UserRoles.PharmacyUser],
      rolesRoutesMapping: {
        [UserRoles.CMExternal]: '/care/patients',
        [UserRoles.CMInternal]: '/care-management/patients',
        [UserRoles.CareTeamHRA]: '/care/patients',
        [UserRoles.TenantAdmin]: '/adherence/pharmacies',
        [UserRoles.HealthAssessmentViewer]: '/external/members',
      },
      dashboardFeatureFallbackRoute: '/adherence',
    },
    title: 'Dashboard',
  },
  {
    path: '**',
    pathMatch: 'full',
    resolve: {
      layout: setLayout(Layouts.Minimal),
    },
    component: AccessDeniedShellComponent,
  },
];
